import { z } from "zod";
import { isValidCnpj } from "./cnpj.js";
import { normalizePhoneBR } from "./phone.js";
import { SLUG_RE } from "./slug.js";
import { CUSTOM_DOMAIN_STATES } from "./custom-domain.js";
/**
 * Documento `customDomains/{host}` (id = hostname em minúsculas). Índice reverso
 * host → slug do renderer + registro de estado do cert por host. Escrito só pela
 * `painel-farmacia-api` (Admin SDK); as regras negam escrita do client.
 */
export const customDomainDocSchema = z.object({
    slug: z.string().regex(SLUG_RE),
    /** Host canonical do par (o que o suporte digitou). */
    primaryHost: z.string(),
    role: z.enum(["primary", "secondary"]),
    status: z.enum(CUSTOM_DOMAIN_STATES),
    detail: z.string().nullable().default(null),
    /** Nomes de recurso no Certificate Manager (para deprovisionar). */
    certificateName: z.string().default(""),
    certificateMapEntryName: z.string().default(""),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
    checkedAt: z.string().datetime().optional(),
});
export const WEEKDAYS = ["seg", "ter", "qua", "qui", "sex", "sab", "dom", "feriado"];
export const WEEKDAY_LABEL = {
    seg: "Segunda",
    ter: "Terça",
    qua: "Quarta",
    qui: "Quinta",
    sex: "Sexta",
    sab: "Sábado",
    dom: "Domingo",
    feriado: "Feriados",
};
const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
export const horarioSchema = z.object({
    dia: z.enum(WEEKDAYS),
    abre: z.string().regex(HHMM, "Use HH:MM"),
    fecha: z.string().regex(HHMM, "Use HH:MM"),
});
export const enderecoSchema = z.object({
    logradouro: z.string().trim().default(""),
    numero: z.string().trim().default(""),
    complemento: z.string().trim().optional(),
    bairro: z.string().trim().default(""),
    cidade: z.string().trim().default(""),
    uf: z.string().trim().toUpperCase().default(""),
    cep: z.string().trim().default(""),
});
/** Ícones disponíveis para os cards de serviço (renderizados como SVG inline). */
export const SERVICO_ICONES = [
    "mais",
    "pilula",
    "estetoscopio",
    "seringa",
    "pressao",
    "entrega",
    "higiene",
    "coracao",
    "pagamento",
    "receita",
    "vacina",
    "teste",
];
export const SERVICO_ICONE_LABEL = {
    mais: "Cruz (genérico)",
    pilula: "Pílula / medicamento",
    estetoscopio: "Estetoscópio / assistência",
    seringa: "Seringa / injetável",
    pressao: "Pressão arterial",
    entrega: "Entrega / delivery",
    higiene: "Higiene e beleza",
    coracao: "Saúde e bem-estar",
    pagamento: "Pagamento / convênio",
    receita: "Receita / prescrição",
    vacina: "Vacina",
    teste: "Teste / exame",
};
export const servicoSchema = z.object({
    titulo: z.string().trim().min(1),
    descricao: z.string().trim().optional(),
    icone: z.enum(SERVICO_ICONES).default("mais"),
});
export const logoSchema = z.object({
    path: z.string().min(1),
    url: z.string().url(),
    contentType: z.string().default(""),
});
/**
 * Foto enviada pelo suporte (hero e fachada). Mesmo formato do logo, mais o
 * texto alternativo — que é obrigatório na prática para acessibilidade, mas
 * tolerado em branco no rascunho (o render cai num alt genérico).
 */
export const fotoSchema = z.object({
    path: z.string().min(1),
    url: z.string().url(),
    contentType: z.string().default(""),
    alt: z.string().trim().default(""),
});
export const farmaceuticoSchema = z.object({
    nome: z.string().trim().default(""),
    crf: z.string().trim().default(""),
});
/** Linha em branco recém-adicionada no painel — não vale como cadastro. */
const farmaceuticoVazio = (f) => !f.nome && !f.crf;
/**
 * Aceita os documentos gravados antes de `cnpj`/`farmaceuticoResponsavel`
 * virarem lista. Os campos antigos são convertidos na leitura e somem do
 * documento na primeira gravação — a migração acontece sozinha, sem script.
 */
function migrarCamposLegados(input) {
    if (typeof input !== "object" || input === null || Array.isArray(input))
        return input;
    const doc = { ...input };
    if (doc.cnpjs === undefined && typeof doc.cnpj === "string") {
        doc.cnpjs = doc.cnpj.trim() ? [doc.cnpj] : [];
    }
    if (doc.farmaceuticos === undefined && doc.farmaceuticoResponsavel !== undefined) {
        const legado = doc.farmaceuticoResponsavel;
        const temNome = typeof legado === "object" && legado !== null && !!legado.nome;
        doc.farmaceuticos = temNome ? [legado] : [];
    }
    return doc;
}
/**
 * Documento como fica guardado no Firestore (`farmacias/{slug}`).
 * Tolerante: um rascunho pode ter quase tudo em branco. A trava de publicação
 * é a `checkPublishReady` / `farmaciaPublishSchema`.
 */
export const farmaciaSchema = z.preprocess(migrarCamposLegados, z.object({
    slug: z.string().regex(SLUG_RE),
    status: z.enum(["draft", "published"]).default("draft"),
    // Checklist §4 (obrigatórios para publicar)
    razaoSocial: z.string().trim().default(""),
    nomeFantasia: z.string().trim().default(""),
    /**
     * CNPJs da empresa — matriz e filiais aparecem juntos no rodapé. Entradas em
     * branco são descartadas na gravação.
     */
    cnpjs: z
        .array(z.string().trim())
        .transform((itens) => itens.filter(Boolean))
        .default([]),
    endereco: enderecoSchema.default({}),
    telefoneComercial: z.string().trim().default(""),
    whatsapp: z.string().trim().default(""),
    /** Responsáveis técnicos. Uma farmácia pode ter mais de um por turno. */
    farmaceuticos: z
        .array(farmaceuticoSchema)
        .transform((itens) => itens.filter((f) => !farmaceuticoVazio(f)))
        .default([]),
    horarios: z.array(horarioSchema).default([]),
    logo: logoSchema.nullable().default(null),
    // Opcionais
    email: z.union([z.string().email(), z.literal("")]).default(""),
    servicos: z.array(servicoSchema).default([]),
    descricaoCurta: z.string().trim().default(""),
    licencaSanitaria: z.string().trim().default(""), // licença sanitária / AFE
    // Conteúdo editorial da landing (tudo opcional — o template tem fallback
    // derivado dos dados cadastrais para cada um destes).
    /** Selo acima do H1, ex.: "Há mais de 30 anos em Nuporanga". */
    badge: z.string().trim().default(""),
    /** H1 do hero. Trechos entre *asteriscos* saem na cor da marca. */
    headline: z.string().trim().default(""),
    /** Texto da seção "Sobre" — parágrafos separados por linha em branco. */
    sobre: z.string().trim().default(""),
    /**
     * Bullets com check no hero, ex.: "Entrega gratuita". Itens em branco (linha
     * recém-adicionada no painel) são descartados na hora de gravar.
     */
    diferenciais: z
        .array(z.string().trim())
        .transform((itens) => itens.filter(Boolean))
        .default([]),
    /** @ ou URL do Instagram. */
    instagram: z.string().trim().default(""),
    fotoHero: fotoSchema.nullable().default(null),
    fotoFachada: fotoSchema.nullable().default(null),
    theme: z
        .object({ cor: z.string().regex(/^#[0-9a-fA-F]{6}$/, "Cor hex, ex: #0F7B4A") })
        .nullable()
        .default(null),
    // Domínio próprio (ADR-025 Fase 2). `customDomain` = host canonical (o que o
    // suporte digitou, ex.: `www.drogariax.com.br`); o par www↔apex e o estado
    // por host vivem na coleção `customDomains/{host}`, gerida pela painel-api.
    customDomain: z.string().nullable().default(null),
    customDomainStatus: z.enum(CUSTOM_DOMAIN_STATES).default("none"),
    createdAt: z.string().datetime().optional(),
    updatedAt: z.string().datetime().optional(),
}));
/** Parse tolerante de um doc vindo do Firestore. */
export function parseFarmacia(input) {
    return farmaciaSchema.parse(input);
}
export { isValidCnpj, normalizePhoneBR };
//# sourceMappingURL=farmacia.js.map